import { useEffect, useRef, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Button } from './Button';

interface EndSessionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function EndSessionDialog({ open, onClose, onConfirm }: EndSessionDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const endingRef = useRef(false);
  const [ending, setEnding] = useState(false);

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) {
      endingRef.current = false;
      setEnding(false);
      return;
    }
    previousFocus.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !endingRef.current) onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previousFocus.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  const confirm = () => {
    if (endingRef.current) return;
    endingRef.current = true;
    setEnding(true);
    onConfirm();
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/55 p-4 backdrop-blur-sm">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="end-session-title"
        aria-describedby="end-session-description"
        aria-busy={ending}
        tabIndex={-1}
        className="focus-ring w-full max-w-md rounded-md border border-red-300/35 bg-slate-950 p-6 text-slate-50 shadow-2xl"
      >
        <div className="flex items-start gap-4">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-red-500/15 text-red-300"><ShieldAlert className="h-6 w-6" /></div>
          <div>
            <h2 id="end-session-title" className="text-xl font-bold">End this setup session?</h2>
            <p id="end-session-description" className="mt-3 leading-7 text-slate-200">The phone camera will disconnect and the current setup progress will be cleared.</p>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <Button variant="secondary" disabled={ending} onClick={onClose}>Keep Session</Button>
          <Button variant="danger" loading={ending} onClick={confirm}>End Session</Button>
        </div>
      </div>
    </div>
  );
}
